import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import SupportBackLink from '../../components/support/SupportBackLink';
import KnowledgeBase from '../../components/support/KnowledgeBase';
import { representativeKnowledgeApi } from '../../services/supportKnowledgeApi';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

// Base di conoscenza per i rappresentanti (Task 29): solo le schede dei canali
// di cui si e componente. Guardia sul permesso di lettura (il backend risponde
// comunque 403); la gestione richiede associativa.supporto.knowledge_manage.
const AssociativeSupportConoscenza: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState<RepProfile | null>(null);
  const has = (name: string) => profile?.permissions?.some((p) => p.name === name) ?? false;
  const canLive = has('associativa.live.publish');
  const canRead = has('associativa.supporto.knowledge_read');
  const canManage = has('associativa.supporto.knowledge_manage');

  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <AssociativeAppShell
      route="supporto"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
    >
      <div className="page fade-in">
        <SupportBackLink to="/area-associativa/supporto" label="Torna al supporto" />
        {!profile ? null : canRead ? (
          <KnowledgeBase
            api={representativeKnowledgeApi}
            canManage={canManage}
            variant="associative"
            initialCardId={params.get('scheda')}
          />
        ) : (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Funzione non abilitata per la tua carica</p>
            <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">
              La Base di conoscenza non è attiva per il tuo ruolo associativo. Se ti serve, chiedi alla segreteria nazionale di abilitarla dai permessi associativi.
            </p>
          </div>
        )}
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeSupportConoscenza;
